'use client';

import { useRef } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER, Stamp } from './bits';
import DossierHead from './DossierHead';
import { stampIn, useChoreo } from './motion';

/**
 * §06 — migration (hairline band). Three concrete steps, then the amber
 * friction panel that names what does NOT come across (honesty rail: the CSV
 * importer is a dated roadmap pill from lib/roadmap.ts, never a feature claim).
 */
export default function MigrationSection({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-step]'), {
      y: 26,
      opacity: 0,
      duration: 0.7,
      stagger: 0.14,
    });
    tl.from(root.querySelector('[data-friction]'), { y: 18, opacity: 0, duration: 0.6 }, '-=0.25');
    stampIn(tl, root.querySelector('[data-friction-pill]'), '-=0.2');
    return tl;
  });

  return (
    <section ref={rootRef} className="mt-16 border-y border-line-2 md:mt-24">
      <div className={`${CONTAINER} py-14 md:py-[88px]`}>
        <DossierHead num="06" label="MIGRATION" title={data.migration.title} />
        <div className="mt-8 flex flex-wrap gap-6 md:mt-11">
          {data.migration.steps.map((step) => (
            <div
              key={step.step}
              data-step
              className="flex min-w-[250px] flex-[1_1_300px] flex-col gap-2.5 rounded-ref border border-line-2 bg-surface px-[26px] py-6"
            >
              <span className="font-jetbrains text-[11px] font-semibold tracking-[0.1em] text-brand-600">
                {step.step}
              </span>
              <h3 className="font-onest text-[16.5px] font-bold tracking-[-0.01em]">
                {step.title}
              </h3>
              <p className="text-[14px] leading-[1.6] text-pretty text-muted">{step.body}</p>
              <span className="mt-auto border-t border-line pt-2.5 font-jetbrains text-[10.5px] text-dim">
                {step.readout}
              </span>
            </div>
          ))}
        </div>
        <div
          data-friction
          className="mt-6 flex flex-col gap-2.5 rounded-ref border border-[color-mix(in_srgb,var(--color-data-amber)_38%,transparent)] bg-[color-mix(in_srgb,var(--color-data-amber)_7%,var(--color-surface))] px-[26px] py-[22px]"
        >
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-jetbrains text-[10px] font-semibold tracking-[0.14em] text-ink">
              {data.migration.friction.label}
            </span>
            <span data-friction-pill className="ml-auto">
              <Stamp tone="amber">{data.migration.friction.pill}</Stamp>
            </span>
          </div>
          <p className="max-w-[860px] text-[14px] leading-[1.65] text-pretty text-muted">
            {data.migration.friction.body}
          </p>
        </div>
      </div>
    </section>
  );
}
